import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Lock, Calendar, AlertCircle, ArrowRight } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

export function VerificationStep({ needsPassword, password, setPassword, birthDay, setBirthDay, birthMonth, setBirthMonth, birthYear, setBirthYear, remainingAttempts, submitting, onSubmit, onBack }) {
  const { t } = useTranslation();

  return (
    <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="bg-white border border-slate-200 rounded-xl p-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
          {needsPassword ? <Lock className="h-6 w-6 text-blue-600" /> : <Calendar className="h-6 w-6 text-blue-600" />}
        </div>
        <div>
          <h3 className="text-xl font-heading font-semibold text-slate-900">{t('voting.verification')}</h3>
          <p className="text-sm text-slate-500">{needsPassword ? t('voting.enterPassword') : t('voting.enterBirthDate')}</p>
        </div>
      </div>

      {remainingAttempts < 5 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-amber-600" />
          <span className="text-sm text-amber-800">{t('voting.remainingAttempts', { count: remainingAttempts })}</span>
        </div>
      )}

      <form onSubmit={onSubmit}>
        <div className="form-group mb-6">
          {needsPassword ? (
            <>
              <Label htmlFor="password" className="form-label">{t('voting.password')}</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="--------" className="h-12" data-testid="password-input" />
            </>
          ) : (
            <>
              <Label className="form-label">{t('voting.birthDate')}</Label>
              <div className="flex gap-2 items-center">
                <Input inputMode="numeric" pattern="[0-9]*" maxLength={2} placeholder="DD" value={birthDay}
                  onChange={(e) => { const v = e.target.value.replace(/\D/g, '').slice(0, 2); setBirthDay(v); if (v.length === 2) e.target.nextElementSibling?.nextElementSibling?.focus(); }}
                  className="h-12 text-center w-16" data-testid="birth-day-input-2" />
                <span className="text-slate-400 text-lg">/</span>
                <Input inputMode="numeric" pattern="[0-9]*" maxLength={2} placeholder="MM" value={birthMonth}
                  onChange={(e) => { const v = e.target.value.replace(/\D/g, '').slice(0, 2); setBirthMonth(v); if (v.length === 2) e.target.nextElementSibling?.nextElementSibling?.focus(); }}
                  className="h-12 text-center w-16" data-testid="birth-month-input-2" />
                <span className="text-slate-400 text-lg">/</span>
                <Input inputMode="numeric" pattern="[0-9]*" maxLength={4} placeholder="AAAA" value={birthYear}
                  onChange={(e) => setBirthYear(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  className="h-12 text-center w-24" data-testid="birth-year-input-2" />
              </div>
            </>
          )}
        </div>
        <div className="flex gap-3">
          <Button type="button" variant="outline" onClick={onBack} className="flex-1">{t('voting.back')}</Button>
          <Button type="submit" className="flex-1 bg-slate-900 hover:bg-slate-800" data-testid="verify-identity-btn">
            {t('voting.continue')}<ArrowRight className="h-5 w-5 ml-2" />
          </Button>
        </div>
      </form>
    </motion.div>
  );
}
